import {
  BMButton,
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { addDays, differenceInDays, isBefore } from "date-fns";
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { StyleSheet } from "react-native";
import { BMSnackbarManager } from "../../../components/BMSnackbar/BMSnackbar";
import BMView from "../../../components/View/BMView";
import { useLocalizedDateFormat } from "../../../core";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { appendTestId, formatMessage } from "../../../utils";
import CalendarPickerFullScreenModal from "../common/CalendarPicker/CalendarPickerFullScreenModal";
import {
  MAX_CHECKOUT_DATE,
  MAX_STAY_DURATION,
  NEXT_YEAR_DATE,
} from "./constants";
import { HotelCalendarPickerFormValues, HotelSearchFormValues } from "./types";

type Props = {
  isVisible: boolean;
  closeDialog: () => void;
  initialValues: HotelCalendarPickerFormValues;
};

const testID = "hotel-calendar_picker";

export default function CalendarPicker(props: Props) {
  const { isVisible, closeDialog, initialValues } = props;
  const { format } = useLocalizedDateFormat();
  const { BMComponents } = useBMTheme();

  const { CorporateHotelSearchForm: cr } = useMobileContentResource();
  const { setValue } = useFormContext<HotelSearchFormValues>();

  const methods = useForm<HotelCalendarPickerFormValues>({
    defaultValues: initialValues,
  });

  const { reset, control } = methods;

  const [checkInDate, checkOutDate] = useWatch({
    control,
    name: ["checkInDate", "checkOutDate"],
  });

  function handleStayDurationInvalid() {
    const maxDate = format(
      addDays(checkInDate, MAX_STAY_DURATION),
      "FULL_MONTH"
    );

    BMSnackbarManager.show({
      title: cr.maxDurationTitle,
      message: formatMessage(cr.maxDurationDescription, {
        maxNight: MAX_STAY_DURATION,
        date: maxDate,
      }),
      type: "YELLOW",
    });
  }

  function handleCheckInDateInvalid() {
    BMSnackbarManager.show({
      title: cr.maxBookingPeriodTitle,
      message: formatMessage(cr.maxBookingPeriodDescription, {
        maxDate: format(NEXT_YEAR_DATE, "FULL_MONTH"),
      }),
      type: "YELLOW",
    });
  }

  function handleCloseButtonClicked() {
    reset(initialValues);
    closeDialog();
  }

  function handleSaveCalendar() {
    if (!checkOutDate) return;

    const stayDuration = differenceInDays(checkOutDate, checkInDate);

    // Check if check-in date is before a year from now
    const isCheckInDateValid = isBefore(checkInDate, NEXT_YEAR_DATE);
    if (!isCheckInDateValid) {
      handleCheckInDateInvalid();
      return;
    }

    // Check if stay duration is less than the MAX_STAY_DURATION
    const isStayDurationValid = stayDuration <= MAX_STAY_DURATION;
    if (!isStayDurationValid) {
      handleStayDurationInvalid();
      return;
    }

    setValue("checkInDate", checkInDate);
    setValue("checkOutDate", checkOutDate);
    closeDialog();
    return;
  }

  const dateLabelMap = [
    {
      label: cr.checkInLabel,
      value: format(checkInDate, "SHORT_MONTH"),
    },
    {
      label: cr.checkOutLabel,
      value: checkOutDate ? format(checkOutDate, "SHORT_MONTH") : "-",
    },
  ];

  const styles = createStyles(BMComponents);

  return (
    <FormProvider {...methods}>
      <CalendarPickerFullScreenModal
        maxDate={MAX_CHECKOUT_DATE}
        onClose={handleCloseButtonClicked}
        title={cr.calendarPickerTitle}
        name={["checkInDate", "checkOutDate"]}
        visible={isVisible}
        footerComponent={
          <BMView style={styles.footer} row align="center" justify="between">
            {dateLabelMap.map(({ label, value }) => (
              <BMView spacing="xs">
                <BMText variant="uiNormal">{label}</BMText>
                <BMText variant="paragraphNormal">{value}</BMText>
              </BMView>
            ))}
            <BMView>
              <BMButton
                disabled={!checkOutDate}
                label={cr.confirm}
                role="PRIMARY"
                onPress={handleSaveCalendar}
                size="NORMAL"
                testID={appendTestId(testID, "confirm_button")}
              />
            </BMView>
          </BMView>
        }
      />
    </FormProvider>
  );
}

const createStyles = (BMComponents: BMComponentsType) =>
  StyleSheet.create({
    footer: {
      width: "100%",
      padding: BMPrimitives.BMSize.md,
      paddingBottom: BMPrimitives.BMSize.sm,
      borderStyle: "solid",
      borderTopColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderTopWidth: BMPrimitives.BMStroke.sm,
    },
  });
